import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { TextFieldProps, Theme } from '@material-ui/core';
import { TextField } from '@material-ui/core';
import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import type { NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { useSimpleDebounce } from '@forvalter/utils/useSimpleDebounce';

type NumericFieldProps = {
    isRequired: boolean;
    isReadOnly: boolean;
    onValueChange?: (values: { value: string; floatValue?: number }) => void;
    debounceDelay?: number; // Optional debounce delay in ms
} & Omit<NumericFormatProps<TextFieldProps>, 'onValueChange'>;

const styles = (theme: Theme) => ({
    input: {
        borderRadius: theme.spacing(0.5),
        '& .MuiInputBase-input': {
            textAlign: 'right',
            paddingRight: theme.spacing(1),
        },
        '&:focus-within': {
            //outline: '1px solid red',
            boxShadow: `0 0 0 2px ${theme.palette.tertiary?.medium}`,
            '& .MuiInputBase-root:before': {
                display: 'none',
            },
            '& .MuiInputBase-root:after': {
                display: 'none',
            },
        },
    },
});

const NumericFieldComponent: React.FC<NumericFieldProps> = ({
    id,
    label,
    placeholder,
    isRequired,
    isReadOnly,
    value,
    onValueChange,
    onFocus: _onFocus,
    debounceDelay = 300, // Default to 300ms
    ...otherProps
}) => {
    const classes = useKlpStyles({}, styles);
    const inputRef = useRef<HTMLInputElement>(null);
    const onValueChangeRef = useRef(onValueChange);

    // Local state for immediate UI feedback
    const [localValue, setLocalValue] = useState(
        value === 0 || value === '0' ? '' : (value ?? ''),
    );

    // Sync with external value changes (from API responses)
    useEffect(() => {
        setLocalValue(value === 0 || value === '0' ? '' : (value ?? ''));
    }, [value]);

    // Update ref when onValueChange prop changes
    useEffect(() => {
        onValueChangeRef.current = onValueChange;
    }, [onValueChange]);

    // Debounced callback for context updates
    const debouncedContextUpdate = useSimpleDebounce(
        (values: { value: string; floatValue?: number }) => {
            if (!onValueChangeRef.current) {
                return;
            }

            onValueChangeRef.current({
                value: values.value,
                floatValue:
                    values.floatValue !== undefined
                        ? values.floatValue
                        : undefined,
            });
        },
        debounceDelay,
    );

    const handleChange = useCallback(
        (values: { value: string; floatValue?: number }) => {
            // Update local state immediately
            setLocalValue(values.value);

            // If empty, send 0 to parent; otherwise send the actual value
            const valueToSend = values.value === '' ? '0' : values.value;
            const floatValueToSend =
                (values.value === '' ? 0 : values.floatValue) ?? 0;

            // Trigger debounced context update
            debouncedContextUpdate({
                value: valueToSend,
                floatValue: floatValueToSend,
            });
        },
        [debouncedContextUpdate],
    );

    const stableInputProps = useMemo(
        () => ({
            'aria-readonly': isReadOnly,
        }),
        [isReadOnly],
    );

    const stableInputLabelProps = {
        shrink: true,
    };

    return (
        <NumericFormat
            inputRef={inputRef}
            thousandSeparator={' '}
            decimalSeparator=","
            value={localValue}
            id={id}
            customInput={TextField}
            label={label}
            placeholder={placeholder || '0'}
            type="text"
            fullWidth={true}
            multiline={true}
            required={isRequired}
            inputProps={stableInputProps}
            disabled={isReadOnly}
            InputLabelProps={stableInputLabelProps}
            className={classes.input}
            onValueChange={handleChange}
            onFocus={_onFocus}
            {...otherProps}
        />
    );
};

NumericFieldComponent.displayName = 'NumericField';
export const NumericField = React.memo(NumericFieldComponent);
